import React, { useCallback, useEffect, useRef } from 'react';
import QuotesTableRow from './QuotesTableRow';
import { Quote } from '../../../../api/quotes';
import './QuotesTable.scss';

interface QuotesTableProps {
    quotesList: Quote[];
    hasMore: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => void;
    onView: (quote: Quote) => void;
    onEdit: (quote: Quote) => void;
    onDelete: (quote: Quote) => void;
}

const QuotesTable: React.FC<QuotesTableProps> = ({
    quotesList,
    hasMore,
    isLoadingMore,
    onLoadMore,
    onView,
    onEdit,
    onDelete
}) => {
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const scrollTimeoutRef = useRef<number | null>(null);

    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current || !hasMore || isLoadingMore) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const threshold = 100;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
        const isContentSmallerThanContainer = scrollHeight <= clientHeight + threshold;

        if (isNearBottom || isContentSmallerThanContainer) {
            onLoadMore();
        }
    }, [hasMore, isLoadingMore, onLoadMore]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const onScroll = () => {
            if (scrollTimeoutRef.current) {
                window.clearTimeout(scrollTimeoutRef.current);
            }
            scrollTimeoutRef.current = window.setTimeout(() => {
                handleScroll();
            }, 100);
        };

        container.addEventListener('scroll', onScroll, { passive: true });
        
        if (hasMore && quotesList.length > 0) {
            setTimeout(() => handleScroll(), 100);
        }

        return () => {
            container.removeEventListener('scroll', onScroll);
            if (scrollTimeoutRef.current) {
                window.clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [handleScroll, hasMore, quotesList.length]);

    return (
        <div className='quotes-container'>
            <div className='quotes-table'>
                <div className='table-body' ref={scrollContainerRef}>
                    <div className='table-header'>
                        <div className='header-cell'>Quote</div>
                        <div className='header-cell'>Plans</div>
                        <div className='header-cell actions'></div>
                    </div>
                    {quotesList?.length > 0 ? (
                        quotesList.map((quote) => (
                            <QuotesTableRow
                                quote={quote}
                                key={quote.id}
                                onView={() => onView(quote)}
                            />
                        ))
                    ) : (
                        <div className='empty-state' style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            No quotes found.
                        </div>
                    )}
                    {isLoadingMore && quotesList.length > 0 && (
                        <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                            Loading more quotes...
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuotesTable;
